
// Utility functions for stable target generation
import { targetService } from '../services/targetService';
import { locationTargetService } from '../services/locationTargetService';

export interface TargetGenerationResult {
  success: boolean;
  targetCode?: string;
  targetType?: string;
  targetUrl?: string;
  message?: string;
  error?: string;
  unsplashData?: {
    id: string;
    photographer: string;
    photographerUsername: string;
    photographerUrl: string;
    unsplashUrl: string;
    downloadUrl: string;
    downloadEndpoint: string;
    description: string;
  };
}

// Single source of truth for target generation detection
export const isTargetGenerationRequest = (content: string): boolean => {
  // First, explicitly check for feedback/analysis markers. If found, it's NOT a generation request.
  const feedbackMarkers = [
    '[SYSTEM-MODE: ANALYSIS-ONLY]',
    '--- CONTEXT FOR ANALYSIS ---',
    'INSTRUCTIONS FOR VISUAL ANALYSIS',
    'INSTRUCTIONS FOR VIEW ANALYSIS',
    "analyze the user's notes", // from reveal handler
  ];
  if (feedbackMarkers.some(marker => content.includes(marker))) {
    console.log('🎯 isTargetGenerationRequest: false (feedback marker found)');
    return false;
  }

  // Second, check for explicit generation commands. These patterns are less ambiguous
  // and focus on user intent to get a new target.
  const generationPatterns = [
    /generate a(nother)?/i,
    /create a(nother)?/i,
    /(give me|get me) a(nother)?/i,
    /new target/i,
    /another target/i,
    /(location|image|prediction) target/i, // Handle direct requests like "location target", "image target", "prediction target"
  ];

  // A request must contain a generation keyword AND the word "target".
  const isRequest = generationPatterns.some(p => p.test(content)) && /target/i.test(content);

  if (isRequest) {
    console.log('🎯 isTargetGenerationRequest: true (generation pattern matched)');
  }

  return isRequest;
};

export const getTargetTypeFromRequest = (content: string): string | null => {
  const lowerContent = content.toLowerCase();
  if (lowerContent.includes('location')) return 'location';
  if (lowerContent.includes('image')) return 'image';
  if (lowerContent.includes('prediction')) return 'prediction';
  
  // If no specific type mentioned, ask the user to specify
  return null;
};

// Generate target based on type
export const generateTargetByType = async (targetType: string, sessionId?: string): Promise<TargetGenerationResult> => {
  try {
    console.log('🎯 targetGenerationUtils: Generating target of type:', targetType);
    
    if (targetType === 'location') {
      console.log('🗺️ targetGenerationUtils: Calling location target service...');
      
      // Increased timeout for location generation
      const locationTarget = await Promise.race([
        locationTargetService.generateLocationTarget(),
        new Promise<null>((_, reject) => 
          setTimeout(() => reject(new Error('Location target generation timed out after 45 seconds')), 45000)
        )
      ]);
      
      if (locationTarget) {
        console.log('✅ targetGenerationUtils: Location target generated successfully:', locationTarget.targetCode);
        return {
          success: true,
          targetCode: locationTarget.targetCode,
          targetType: 'location',
          targetUrl: locationTarget.targetUrl,
          message: `Target Code: **${locationTarget.targetCode}**

A new location target has been generated for remote viewing. You can share impressions in the chat or upload your sketches to log and timestamp them before clicking 'Reveal Target' when you're ready.`
        };
      } else {
        console.log('❌ targetGenerationUtils: Location target generation returned null, falling back to image');
        return await generateImageTarget();
      }
    } else if (targetType === 'image') {
      return await generateImageTarget();
    } else if (targetType === 'prediction') {
      return await generatePredictionTarget(sessionId);
    }
    
    return { success: false, error: 'Invalid target type' };
  } catch (error) {
    console.error('❌ targetGenerationUtils: Error generating target:', error);
    
    // If location generation fails, fall back to image target
    if (targetType === 'location') {
      console.log('🔄 targetGenerationUtils: Location generation failed, falling back to image target');
      return await generateImageTarget();
    }
    
    return { 
      success: false, 
      error: `Target generation failed: ${error.message}. Please try again.` 
    };
  }
};

// Generate image target using Unsplash API
const generateImageTarget = async (): Promise<TargetGenerationResult> => {
  try {
    console.log('🎯 Generating image target using Unsplash...');
    
    // Call the Unsplash edge function
    const response = await fetch('https://qpnvlfrytztskaayfsxu.supabase.co/functions/v1/unsplash-target', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.error) {
      throw new Error(data.error);
    }
    
    console.log('✅ Generated Unsplash image target:', { 
      targetCode: data.targetCode, 
      photographer: data.unsplashData?.photographer 
    });
    
    return {
      success: true,
      targetCode: data.targetCode,
      targetType: 'image',
      targetUrl: data.imageUrl,
      unsplashData: data.unsplashData,
      message: `Target Code: **${data.targetCode}**

A new image target has been generated for remote viewing. You can share impressions in the chat or upload your sketches to log and timestamp them before clicking 'Reveal Target' when you're ready.`
    };
  } catch (error) {
    console.error('❌ Error generating Unsplash image target:', error);
    
    // Fallback to a simple placeholder if Unsplash fails
    const targetCode = generateTargetCode();
    const imageId = Math.floor(Math.random() * 1000) + 1;
    const targetUrl = `https://picsum.photos/800/600?random=${imageId}`;
    
    return {
      success: true,
      targetCode,
      targetType: 'image',
      targetUrl,
      message: `Target Code: **${targetCode}**

A new image target has been generated for remote viewing (using fallback). You can share impressions in the chat or upload your sketches to log and timestamp them before clicking 'Reveal Target' when you're ready.`
    };
  }
};

// Generate prediction target using edge function
const generatePredictionTarget = async (sessionId?: string): Promise<TargetGenerationResult> => {
  try {
    console.log('🎯 Generating prediction target...');
    
    if (!sessionId) {
      throw new Error('No active session found. Please create a new chat session.');
    }
    
    console.log('🎯 Using session ID:', sessionId);
    
    // Import supabase client dynamically to avoid circular dependencies
    const { supabase } = await import('@/integrations/supabase/client');
    
    const { data, error } = await supabase.functions.invoke('generate-prediction-target', {
      body: { sessionId }
    });

    if (error) {
      console.error('❌ Supabase function error:', error);
      throw new Error(error.message || 'Failed to generate prediction target');
    }
    
    // Handle error responses from the edge function
    if (data?.error) {
      console.error('❌ Edge function returned error:', data);
      const errorMessage = data.details || data.error;
      throw new Error(errorMessage);
    }
    
    // Handle non-2xx status codes
    if (!data?.success && !data?.targetCode) {
      throw new Error('Edge Function returned a non-2xx status code');
    }
    
    console.log('✅ Generated prediction target:', { 
      targetCode: data.targetCode,
      eventTitle: data.eventTitle 
    });
    
    return {
      success: true,
      targetCode: data.targetCode,
      targetType: 'prediction',
      targetUrl: 'prediction', // Special marker for prediction targets
      message: data.message
    };
  } catch (error) {
    console.error('❌ Error generating prediction target:', error);
    
    return {
      success: false,
      error: error.message || 'Failed to generate prediction target'
    };
  }
};

// Generate target code
const generateTargetCode = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < 8; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  // Format as XXXX-XXXX for display
  return result.substring(0, 4) + '-' + result.substring(4);
};

// Centralized JSON target extraction with validation
export const extractTargetFromResponse = (response: string): TargetGenerationResult => {
  try {
    console.log('🎯 targetGenerationUtils: Extracting target from response');
    
    // Clean up response for JSON extraction
    let cleanContent = response.trim();
    
    // Remove markdown code blocks
    if (cleanContent.startsWith('```json')) {
      cleanContent = cleanContent.replace(/^```json\s*/, '').replace(/\s*```$/, '');
    } else if (cleanContent.startsWith('```')) {
      cleanContent = cleanContent.replace(/^```\s*/, '').replace(/\s*```$/, '');
    }

    // Check for JSON pattern
    const jsonMatch = response.match(/json\s*(\{[\s\S]*?\})/);
    if (jsonMatch) {
      cleanContent = jsonMatch[1];
    }

    // Only try to parse if it looks like JSON
    if (cleanContent.startsWith('{') && cleanContent.endsWith('}')) {
      const parsed = JSON.parse(cleanContent);
      
      // Validate required fields for target generation
      if (parsed.target_code && parsed.type && (parsed.target_url || parsed.message)) {
        console.log('✅ targetGenerationUtils: Valid target JSON found');
        return {
          success: true,
          targetCode: parsed.target_code,
          targetType: parsed.type,
          targetUrl: parsed.target_url,
          message: parsed.message || `Target Code: ${parsed.target_code}\nA new ${parsed.type} target has been generated.`
        };
      }
    }
    
    return { success: false, error: 'No valid target data found' };
  } catch (error) {
    console.error('❌ targetGenerationUtils: Error extracting target:', error);
    return { success: false, error: error.message };
  }
};

// Store target with validation and error handling
export const storeTargetSafely = async (
  sessionId: string, 
  targetCode: string, 
  targetType: string, 
  targetUrl: string,
  unsplashData?: any
): Promise<boolean> => {
  try {
    if (!sessionId || !targetCode || !targetType || !targetUrl) {
      console.error('❌ targetGenerationUtils: Missing required target data');
      return false;
    }
    
    console.log('🎯 targetGenerationUtils: Storing target:', { sessionId, targetCode, targetType });
    const target = await targetService.storeTarget(sessionId, targetCode, targetType, targetUrl, unsplashData);
    
    if (target) {
      console.log('✅ targetGenerationUtils: Target stored successfully');
      return true;
    } else {
      console.error('❌ targetGenerationUtils: Target service returned null');
      return false;
    }
  } catch (error) {
    console.error('❌ targetGenerationUtils: Error storing target:', error);
    return false;
  }
};
